import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import { toastApiError } from "./apiError"
import { invalidateDeclaration } from "./declarationsApi"
import { isSupplierWarningError, sendInquiryDirect } from "./inquiryDraftApi"

const BASE = "/material-compliance/declarations"

/**
 * Per-framework inquiry state the declaration screen renders per row.
 *
 * ONLY "sent"/"answered" EVER CROSS THE WIRE. "none" is the absence of a row for
 * a framework (the backend excludes CLOSED/DRAFT rows and returns only the latest
 * row per framework, so "no history" and "no row" are the same wire shape). "draft"
 * is client-only composer state — a supplier-inquiry draft the user has started but
 * not sent yet has no server-side representation in this list.
 */
export type DeclarationInquiryState = "none" | "draft" | "sent" | "answered"

/** One row from GET /declarations/{id}/inquiries — camelCase on the wire, standard
 *  adapter (not skipCaseConversion). `status` is the raw inquiry status (e.g.
 *  "waiting", "reminded") kept alongside the coarse `state` for tooltip detail. */
export interface DeclarationInquiry {
  /** Null for a supplier "question batch" (wave 5 collector, `source_framework`
   *  NULL server-side): several open batches can coexist, so the backend emits
   *  ONE ROW PER INQUIRY for these instead of collapsing to a latest-per-framework
   *  row. `inquiryStateByFramework` below skips these rows for exactly that
   *  reason: a null key would collide across every open batch. Use
   *  `openQuestionBatches` to read them. */
  frameworkSlug: string | null
  state: "sent" | "answered"
  status: string
  inquiryId: string
  reference: string | null
  statementId: string | null
  sentAt: string | null
  answeredAt: string | null
  supplierId: string | null
  supplierName: string | null
  /** The sent question text (the row's server-side email_body), so the sent/answered
   *  pill can show what was actually asked after a reload or in a colleague's
   *  session — not just a date. Null only for the historical shape where the
   *  backend column itself is unset. */
  message: string | null
  /** How many articles the Rückfrage names. Non-zero: approve holds exactly
   *  those back and proceeds; zero: its scope is unproven and approve refuses. */
  articleCount: number
  /** The server's own decision (`inquiry_holds_back`): approve holds the named
   *  articles back and proceeds. False for a dead-address Rückfrage even when it
   *  names articles, since approve still refuses it. */
  holdsBack: boolean
  /** How many stamped `mc_declaration_questions` this row's batch carries. Only
   *  non-zero on a frameworkless row that IS a question batch; a frameworkless
   *  row with 0 is something else entirely and never counts as one (see
   *  `openQuestionBatches`). Always 0 on a per-framework Rückfrage row (the
   *  legacy inquiry kind stage 1 leaves untouched). */
  questionCount: number
}

/**
 * Reads the per-framework Rückfrage state. DELIBERATELY UNCONDITIONAL: there is
 * no caller-supplied gate, and that is the point.
 *
 * An earlier version took `{ enabled: flagValue }` so a caller could skip the
 * request while MC_DECLARATION_INQUIRY was off, since the endpoint 404s until
 * the backend ships. That traded harmless 404 noise for a correctness bug: the
 * flag governs whether a reviewer may RAISE a Rückfrage, so gating the READ on
 * it hid one that had already been sent. The navigator lost its marker, the
 * footer offered Übernehmen, and the block only surfaced as a 409 from the
 * server. The backend leaves this GET ungated for exactly the same reason. If
 * the 404s ever need silencing, use `retry: false` — never hide sent state.
 */
export function useDeclarationInquiries(declarationId: string) {
  return useQuery({
    queryKey: ["mc", "declarations", declarationId, "inquiries"],
    queryFn: async (): Promise<DeclarationInquiry[]> => {
      // Backend returns a bare array (not paginated envelope) — mirrors
      // useDeclarationArticles / useDeclarationStatements.
      return apiClientV2
        .get(`${BASE}/${declarationId}/inquiries`)
        .then((r) => (r.data ?? []) as DeclarationInquiry[])
    },
    enabled: !!declarationId,
  })
}

/** One regulation of GET /declarations/{id}/inquiry-coverage (TUR-1732). */
export interface InquiryRegulationCoverage {
  /** Framework key (`TSCA`, `PROP_65`, …), labelled client-side. */
  key: string
  state: "delivered" | "missing" | "extra"
  /** The replies stating it, oldest first; empty when missing. */
  declarationIds: string[]
  /** An approved reply holds an accepted statement for it. */
  approved: boolean
}

/** What the supplier inquiry a declaration answers asked, against what all its
 *  replies deliver (TUR-1732). Null when there is nothing to compare: no linked
 *  inquiry, one sent before per-inquiry scopes, or regulation settings off. */
export interface DeclarationInquiryCoverage {
  inquiryId: string
  reference: string | null
  regulations: InquiryRegulationCoverage[]
}

export function useDeclarationInquiryCoverage(declarationId: string) {
  return useQuery({
    queryKey: ["mc", "declarations", declarationId, "inquiry-coverage"],
    queryFn: async (): Promise<DeclarationInquiryCoverage | null> =>
      apiClientV2
        .get(`${BASE}/${declarationId}/inquiry-coverage`)
        .then((r) => (r.data ?? null) as DeclarationInquiryCoverage | null),
    enabled: !!declarationId,
  })
}

/** One row per framework: the backend already collapses history to the latest row.
 *  Skips frameworkless rows (question batches, `frameworkSlug: null`): several
 *  can be open at once, so collapsing them into this per-framework map would
 *  either drop all but one under a null key or leak that key downstream to the
 *  navigator/footer/gates, none of which know what to do with it. Read those
 *  rows through `openQuestionBatches` instead. */
export function inquiryStateByFramework(
  rows: DeclarationInquiry[],
): Map<string, DeclarationInquiry> {
  return new Map(
    rows
      .filter(
        (row): row is DeclarationInquiry & { frameworkSlug: string } =>
          row.frameworkSlug !== null,
      )
      .map((row) => [row.frameworkSlug, row]),
  )
}

/** The open (unanswered) supplier "question batches": frameworkless rows
 *  (`source_framework NULL`, wave 5 collector) that actually carry stamped
 *  questions. A frameworkless row with `questionCount === 0` is something
 *  else, not a batch, and an "answered" row is no longer open; both are
 *  excluded. Unlike `inquiryStateByFramework`, this keeps EVERY matching row:
 *  several batches can be open on the same declaration at once (stage 1,
 *  2026-09-17 decision), so there is no "latest" to collapse to. */
export function openQuestionBatches(
  rows: DeclarationInquiry[],
): DeclarationInquiry[] {
  return rows.filter(
    (row) =>
      row.frameworkSlug === null &&
      row.state === "sent" &&
      row.questionCount > 0,
  )
}

/**
 * The UI-facing selector: a framework absent from the rows is "none" (never
 * "draft" — that state exists only in the composer's own local state, before
 * anything has been sent).
 */
export function inquiryStateFor(
  rows: DeclarationInquiry[],
  frameworkSlug: string,
): Exclude<DeclarationInquiryState, "draft"> {
  return inquiryStateByFramework(rows).get(frameworkSlug)?.state ?? "none"
}

/** Articles an OPEN (sent) Rückfrage on this framework holds back on approve; 0
 *  when there is none, when it was answered, or when the server says it does not
 *  hold anything back (no articles, or a dead address that still refuses). */
export function inquiryArticleCountFor(
  rows: DeclarationInquiry[],
  frameworkSlug: string,
): number {
  const row = inquiryStateByFramework(rows).get(frameworkSlug)
  return row?.state === "sent" && row.holdsBack ? row.articleCount : 0
}

/** The two open statuses a supplier can never answer from: a hard bounce and a
 *  spam complaint. Mirrors the server's own `_DEAD_ADDRESS_INQUIRY_STATUSES`
 *  (mc_declaration_review_service.py), which is why approve refuses such a row
 *  outright instead of holding its articles back. */
const DEAD_ADDRESS_INQUIRY_STATUSES = new Set(["undeliverable", "complained"])

/** Whether this framework's OPEN Rückfrage went to a dead address. Needed
 *  ALONGSIDE `inquiryArticleCountFor`, not instead of it: that selector reports
 *  0 for a dead address even when the row names articles (the server sets
 *  `holdsBack` false there), so a caller reading the count alone cannot tell a
 *  bounced Rückfrage from one that never named an article, and would explain
 *  the block with the wrong reason. */
export function inquiryIsDeadAddressFor(
  rows: DeclarationInquiry[],
  frameworkSlug: string,
): boolean {
  const row = inquiryStateByFramework(rows).get(frameworkSlug)
  return row?.state === "sent" && DEAD_ADDRESS_INQUIRY_STATUSES.has(row.status)
}

/** GET /declarations/{id}/inquiries/thread: whether the NEXT review-mask question
 *  raised against this declaration would go out in the SAME email thread the
 *  supplier used to send it (TUR-1211 W5-2), so AskBox can show the note before
 *  the reviewer sends. `subject` is null when `inReply` is false.
 *
 *  Declaration-scoped, not framework-scoped: the thread decision depends only on
 *  the declaration's own email origin, never on which framework the question is
 *  about, same unconditional-read posture as useDeclarationInquiries above. */
export interface DeclarationInquiryThread {
  inReply: boolean
  subject: string | null
}

export function useDeclarationInquiryThread(declarationId: string) {
  return useQuery({
    queryKey: ["mc", "declarations", declarationId, "inquiries", "thread"],
    queryFn: async (): Promise<DeclarationInquiryThread> => {
      return apiClientV2
        .get(`${BASE}/${declarationId}/inquiries/thread`)
        .then((r) => r.data as DeclarationInquiryThread)
    },
    enabled: !!declarationId,
  })
}

export interface SendDeclarationInquiryIn {
  framework: string
  statementId?: string | null
  message: string
  articleNumbers?: string[] | null
  subject?: string | null
  recipientsTo?: string[] | null
  recipientsCc?: string[] | null
  /** MC_NOTES: the supplier's warning notes the user confirmed (see isSupplierWarningError). */
  acknowledgedNoteIds?: string[]
}

/**
 * Error codes POST /declarations/{id}/inquiries can return as 409/422 detail.code.
 * SEND_FAILED (or the send step's own failure code) means the inquiry WAS created
 * server-side but the outbound email failed — `inquiry_id` names the row so the
 * caller can retry via useRetryInquirySend instead of creating a duplicate.
 *
 * FIELD STAYS SNAKE_CASE. apiClientV2's response interceptor only camelCases the
 * SUCCESS path (`response.data`); the error-branch interceptor in apiClient.ts
 * rejects with the axios error untouched, so `error.response.data.detail` is the
 * backend's raw payload. Every existing call site that reads `detail.*` off an
 * axios error in this codebase only ever reads single-word keys (`code`,
 * `message`) where casing is unobservable — this is the first multi-word one, so
 * it is spelled the way it actually arrives.
 */
export type SendDeclarationInquiryErrorCode =
  | "OPEN_INQUIRY_EXISTS"
  | "UNKNOWN_FRAMEWORK"
  | "SEND_FAILED"
  | string

export interface SendDeclarationInquiryErrorDetail {
  code: SendDeclarationInquiryErrorCode
  message?: string
  // Present on OPEN_INQUIRY_EXISTS (the existing open inquiry) and on SEND_FAILED
  // (the just-created row the retry endpoint targets). Raw wire spelling — see above.
  inquiry_id?: string
}

/**
 * POST /declarations/{id}/inquiries — create + send a supplier inquiry for one
 * framework. On success invalidates the declaration prefix so the row's state
 * flips to "sent" everywhere it is read.
 *
 * On error the generic message is toasted (mirrors useDeleteDeclaration), EXCEPT
 * for a 5xx: apiClient.ts's response interceptor already toasts every status >=
 * 500 (see its own "Toast for 5xx errors" branch and errorToast.ts's
 * `toastServerError`), so a 502 SEND_FAILED toasting here too would stack a
 * second, redundant toast on top of it (and AskBox's own inline panel for this
 * same failure). The raw error is still available on the mutation result so a
 * caller can branch on `detail.code` (OPEN_INQUIRY_EXISTS / UNKNOWN_FRAMEWORK /
 * SEND_FAILED) the way ConformityGenerateModule and DeclarationReviewPage
 * already do for their own business-error codes.
 *
 * A 409 SUPPLIER_WARNING (MC_NOTES) is not toasted either: it is no failure but a
 * question for the user, so the caller reads it with isSupplierWarningError and
 * repeats the call with `acknowledgedNoteIds`. The route raises it BEFORE any draft
 * exists, so nothing was created and there is no row to retry.
 */
export function useSendDeclarationInquiry() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: async (
      input: SendDeclarationInquiryIn & { declarationId: string },
    ): Promise<DeclarationInquiry> => {
      const { declarationId, ...body } = input
      return apiClientV2
        .post(`${BASE}/${declarationId}/inquiries`, body)
        .then((r) => r.data as DeclarationInquiry)
    },
    onSuccess: (_data, variables) => {
      invalidateDeclaration(queryClient, variables.declarationId)
    },
    onError: (error) => {
      if (isSupplierWarningError(error)) return
      toastApiError(error, translate, "MC_DECL_V2_ASKBOX_SEND_FAILED")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

/**
 * Retry path for SEND_FAILED: the inquiry row already exists (created by the POST
 * above), only the email dispatch failed, so this resends it through the SAME
 * endpoint the supplier-inquiries composer uses rather than duplicating the call,
 * see sendInquiryDirect (POST /supplier-inquiries/{id}/send). No stage override:
 * a failed first send retries as a first send, never a reminder.
 *
 * `acknowledgedNoteIds` is for the case that the send gate (MC_NOTES) refuses this
 * resend: a warning note added after the failed first send answers 409 SUPPLIER_WARNING
 * until the user confirmed the notes, and the retry then carries their ids. That is not
 * how the create call above gets past its own SUPPLIER_WARNING: the create route raises
 * it before any draft exists (so there is no `inquiry_id` to retry), and the create call
 * is repeated with `acknowledgedNoteIds` instead. A caller reads either refusal with
 * isSupplierWarningError / supplierWarningNotes (inquiryDraftApi.ts).
 */
export function useRetryInquirySend() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: async (input: {
      declarationId: string
      inquiryId: string
      acknowledgedNoteIds?: string[]
    }): Promise<void> => {
      await sendInquiryDirect(input.inquiryId, {
        // The first attempt of this send skips the placeholder-signature check
        // (check_signoff=False on the declaration routes), so its retry does too.
        acknowledgePlaceholderSignature: true,
        ...(input.acknowledgedNoteIds
          ? { acknowledgedNoteIds: input.acknowledgedNoteIds }
          : {}),
      })
    },
    onSuccess: (_data, variables) => {
      invalidateDeclaration(queryClient, variables.declarationId)
    },
    onError: (error) => {
      toastApiError(error, translate, "MC_DECL_V2_ASKBOX_SEND_FAILED")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}
